import { Box, Chip, ListItemButton, ListItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Membership } from 'shared/types';
import { cloneElement, ReactElement, ReactNode } from 'react';

import { SwitchControl } from '../SwitchControl';
import {
  layersDrawerControlItemChildrenSx,
  layersDrawerRowActionSlotSx,
  layersDrawerRowContentSx,
  layersDrawerRowSx,
  layersDrawerRowTitleSx,
  layersDrawerSwitchSx,
} from './layersDrawer.styles';
import {
  getLayersDrawerMembershipChipLabelKey,
  getLayersDrawerMembershipChipProps,
  layersDrawerInlineChipSx,
} from './layersDrawer.membershipChips';

type LayerControlItemProps = {
  title: string;
  subtitle: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  mapLayer: string;
  disabled?: boolean;
  internalOnly?: boolean;
  minMembership?: Membership;
  privateLayer?: boolean;
  action?: ReactNode;
  children?: ReactElement<{ disabled?: boolean }>;
};

export const LayerControlItem = (props: LayerControlItemProps): JSX.Element => {
  const {
    title,
    subtitle,
    checked,
    onChange,
    mapLayer,
    disabled = false,
    internalOnly,
    minMembership = 'public',
    privateLayer = false,
    action,
    children,
  } = props;

  const { t } = useTranslation();

  const handleChange = (): void => {
    if (disabled) return;
    onChange(!checked);
  };

  const secondaryText = disabled ? t('map.layers.disabled') : subtitle;

  const switchControl = (
    <SwitchControl
      disabled={disabled}
      checked={checked}
      onChange={handleChange}
      listItemEdge={false}
      sx={layersDrawerSwitchSx}
      inputProps={{
        'aria-labelledby': t('map.accessibility.switchLayer'),
        'aria-label': `Toggle ${mapLayer}`,
      }}
    />
  );

  const membershipChips = (
    <>
      {minMembership !== 'public' && !privateLayer && (
        <Chip
          {...getLayersDrawerMembershipChipProps(
            t(
              getLayersDrawerMembershipChipLabelKey(
                minMembership === 'pro' ? 'pro' : 'members',
              ),
            ),
            minMembership === 'pro' ? 'pro' : 'members',
          )}
          sx={layersDrawerInlineChipSx}
          data-testid={`${minMembership === 'pro' ? 'pro' : 'members'}-chip`}
        />
      )}
      {internalOnly && (
        <Chip
          {...getLayersDrawerMembershipChipProps(
            t(getLayersDrawerMembershipChipLabelKey('internal')),
            'internal',
          )}
          sx={layersDrawerInlineChipSx}
          data-testid="internal-chip"
        />
      )}
      {privateLayer && (
        <Chip
          {...getLayersDrawerMembershipChipProps(
            t(getLayersDrawerMembershipChipLabelKey('private')),
            'private',
          )}
          sx={layersDrawerInlineChipSx}
          data-testid="private-chip"
        />
      )}
    </>
  );

  return (
    <ListItem disablePadding sx={{ flexDirection: 'column' }}>
      <ListItemButton
        disabled={disabled}
        onClick={handleChange}
        sx={layersDrawerRowSx}
      >
        <Box sx={layersDrawerRowContentSx}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 0.5,
              minWidth: 0,
            }}
            component="span"
          >
            <Typography component="span" sx={layersDrawerRowTitleSx}>
              {title}
            </Typography>
            {membershipChips}
          </Box>
          {Boolean(secondaryText) && (
            <Typography variant="subtitle1" color="text.secondary">
              {secondaryText}
            </Typography>
          )}
        </Box>
        {action ? (
          <Box sx={layersDrawerRowActionSlotSx}>
            {action}
            {switchControl}
          </Box>
        ) : (
          switchControl
        )}
      </ListItemButton>

      {Boolean(children) && checked && (
        <Box sx={layersDrawerControlItemChildrenSx}>
          {cloneElement(children!, { disabled })}
        </Box>
      )}
    </ListItem>
  );
};
